import React, { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useForm } from 'react-hook-form';
import { closeModal, openModal } from 'data/reducers/modalReducer';
import {
  fetchPortfoliosLegacy,
  addPropertyToPortfolioWithState,
  clearPortfolioErrors,
} from 'data/reducers/portfoliosManagementReducer';
import { showToast } from 'data/reducers/toastReducer';
import { MODAL_TYPES } from 'view/ModalManager';
import StandardModal from './StandardModal';
import Spinner from 'components/Spinner';
import { FormSelect } from '../components/Form';

const SelectPortfolioModal = ({ options }) => {
  const dispatch = useDispatch();
  const { propertyId, onConfirm } = options || {};

  const {
    portfolios,
    portfoliosForAdd,
    isLoading,
    error,
    isAddingProperty,
    addPropertyError,
  } = useSelector((state) => state.portfoliosManagement);

  const [isProcessing, setIsProcessing] = useState(false);

  // React Hook Form setup
  const { control, handleSubmit, watch } = useForm({
    defaultValues: {
      portfolioId: '',
    },
    mode: 'onChange',
  });

  // Watch portfolioId to enable/disable submit button
  const selectedPortfolioId = watch('portfolioId');

  // Fetch portfolios; forAddProperty filters to owner/admin only (viewers cannot add)
  useEffect(() => {
    dispatch(clearPortfolioErrors());
    dispatch(fetchPortfoliosLegacy({ forAddProperty: true }));
  }, [dispatch]);

  // Dropdown options from portfoliosForAdd (only portfolios user can add to)
  const portfolioOptions = useMemo(
    () =>
      portfoliosForAdd.map((portfolio) => ({
        value: portfolio.id,
        label: `${portfolio.name}${portfolio.company ? ` (${portfolio.company})` : ''
          }`,
      })),
    [portfoliosForAdd]
  );

  // Form submission handler
  const onSubmit = async (data) => {
    if (!data.portfolioId || !propertyId) return;

    setIsProcessing(true);

    try {
      await dispatch(
        addPropertyToPortfolioWithState(data.portfolioId, propertyId)
      );

      // Find selected portfolio for success message
      const selectedPortfolio = portfoliosForAdd.find(
        (p) => p.id === data.portfolioId
      );

      if (onConfirm) {
        onConfirm();
      }

      // Show success message
      dispatch(
        showToast({
          title: 'Success!',
          body: `Property has been added to "${selectedPortfolio?.name || 'selected'
            }" portfolio successfully.`,
          type: 'success',
          duration: 4000,
        })
      );

      // Close modal
      dispatch(closeModal());
    } catch (error) {
      console.error('Error adding property to portfolio:', error);
      dispatch(
        showToast({
          title: 'Error!',
          body: 'Failed to add property to portfolio. Please try again.',
          type: 'error',
          duration: 4000,
        })
      );
    } finally {
      setIsProcessing(false);
    }
  };

  const handleCreateNewPortfolio = () => {
    // Close current modal and open AddPortfolioModal
    dispatch(closeModal());
    dispatch(
      openModal(MODAL_TYPES.ADD_PORTFOLIO_MODAL, {
        propertyId, // Pass propertyId so we can add it after portfolio creation
        onConfirm: () => {
          if (onConfirm) {
            onConfirm();
          }
        },
      })
    );
  };

  const handleCancel = () => {
    dispatch(closeModal());
  };

  if (isLoading) {
    return (
      <StandardModal
        title="Select Portfolio"
        size="md"
        onClose={handleCancel}
        noFooter
      >
        <div className="modal-body">
          <div className="text-center py-4">
            <Spinner />
            <p className="mt-3 text-muted">Loading portfolios...</p>
          </div>
        </div>
      </StandardModal>
    );
  }

  return (
    <StandardModal
      title="Add Property to Portfolio"
      size="md"
      onClose={handleCancel}
      noFooter
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="modal-body">
          {(error || addPropertyError) && (
            <div className="alert alert-danger mb-4">
              <i className="bi bi-exclamation-triangle me-2"></i>
              {typeof (error || addPropertyError) === 'string'
                ? error || addPropertyError
                : (error || addPropertyError)?.message ||
                'An error occurred. Please try again.'}
            </div>
          )}

          {portfoliosForAdd.length === 0 ? (
            <div className="text-center py-5">
              <i
                className="bi bi-folder-plus text-muted"
                style={{ fontSize: '3rem' }}
              ></i>
              <h5 className="mt-3 text-muted">
                {portfolios.length === 0
                  ? 'No Portfolios Found'
                  : 'No Portfolios You Can Add To'}
              </h5>
              <p className="text-muted mb-4">
                {portfolios.length === 0
                  ? "You haven't created any portfolios yet. Create your first portfolio to get started."
                  : 'You can only add properties to portfolios you own or have admin access to. Shared portfolios where you are a viewer are view-only.'}
              </p>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleCreateNewPortfolio}
              >
                <i className="bi bi-plus-lg me-2"></i>
                Create New Portfolio
              </button>
            </div>
          ) : (
            <>
              {/* Portfolio Selection */}
              <div className="mb-3">
                <FormSelect
                  name="portfolioId"
                  control={control}
                  label="Portfolio"
                  options={portfolioOptions}
                  placeholder="Choose a portfolio..."
                  required
                />
                <div className="form-text">
                  <i className="bi bi-info-circle me-1"></i>
                  Select a portfolio to add this property to.
                </div>
              </div>

              {/* Create New Portfolio Button */}
            </>
          )}
        </div>

        <div className="modal-footer">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={handleCancel}
            disabled={isProcessing}
          >
            Cancel
          </button>

          <div className="text-center">
            <button
              type="button"
              className="btn btn-primary btn-sm"
              onClick={handleCreateNewPortfolio}
              disabled={isProcessing}
            >
              <i className="bi bi-plus-lg me-2"></i>
              Create New Portfolio
            </button>
          </div>

          {portfoliosForAdd.length > 0 && (
            <button
              type="submit"
              className="btn btn-primary"
              disabled={
                !selectedPortfolioId || isProcessing || isAddingProperty
              }
            >
              {isProcessing || isAddingProperty ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm me-2"
                    role="status"
                  />
                  Adding...
                </>
              ) : (
                <>
                  <i className="bi bi-plus-lg me-2" />
                  Add to Portfolio
                </>
              )}
            </button>
          )}
        </div>
      </form>
    </StandardModal>
  );
};

export default SelectPortfolioModal;
